import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Button,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import ReceiptLongRoundedIcon   from '@mui/icons-material/ReceiptLongRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CreditCardRoundedIcon    from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon      from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon   from '@mui/icons-material/PointOfSaleRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetOrders } from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const MONEY_ICONS = {
    transfer: <CreditCardRoundedIcon sx={{ fontSize: 14 }} />,
    cash:     <PaymentsRoundedIcon   sx={{ fontSize: 14 }} />,
    terminal: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />,
};

const MONEY_LABELS = {
    transfer: 'Перевод',
    cash:     'Наличные',
    terminal: 'Терминал',
};

const MONEY_COLORS = {
    transfer: { color: '#818cf8', bg: 'rgba(129,140,248,0.10)' },
    cash:     { color: '#22c55e', bg: 'rgba(34,197,94,0.10)' },
    terminal: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
};

// ─── Предустановленные периоды ────────────────────────────────────────────────
const getPeriods = () => {
    const now = new Date();
    const today = toISO(now);

    const weekStart = new Date(now);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return [
        { label: 'Сегодня',  from: today,               to: today },
        { label: 'Вчера',    from: toISO(yesterday),     to: toISO(yesterday) },
        { label: 'Неделя',   from: toISO(weekStart),     to: today },
        { label: 'Месяц',    from: toISO(monthStart),    to: today },
    ];
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.04 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    "&:hover": {
        bgcolor: 'var(--joy-palette-background-level3)', 
    }
};


// ════════════════════════════════════════════════════════════════════
//  MyOrdersPage
// ════════════════════════════════════════════════════════════════════
export default function MyOrdersPage() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();

    const periods = useMemo(getPeriods, []);

    // ── Состояние ─────────────────────────────────────────────────
    const [orders,       setOrders]       = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [dateFrom,     setDateFrom]     = useState(getMonthStart);
    const [dateTo,       setDateTo]       = useState(() => toISO(new Date()));
    const [activePeriod, setActivePeriod] = useState('Месяц');

    // ── Загрузка ──────────────────────────────────────────────────
    const loadOrders = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;

        const data = await APIGetOrders(token, params);
        if (Array.isArray(data)) setOrders(data);
        setLoading(false);
    }, [token, dateFrom, dateTo]);

    useEffect(() => { loadOrders(); }, [loadOrders]);
    useEffect(() => onRefreshTick(loadOrders), [onRefreshTick, loadOrders]);

    // ── Быстрый выбор периода ─────────────────────────────────────
    const selectPeriod = (p) => {
        setActivePeriod(p.label);
        setDateFrom(p.from);
        setDateTo(p.to);
    };

    // ── Итоги ─────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const transfer = orders.filter(o => o.money_type === 'transfer').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        const cash     = orders.filter(o => o.money_type === 'cash').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        const terminal = orders.filter(o => o.money_type === 'terminal').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        return { transfer, cash, terminal, total: transfer + cash + terminal, count: orders.length };
    }, [orders]);

    // ── Группировка по дате ───────────────────────────────────────
    const grouped = useMemo(() => {
        const map = {};
        const sorted = [...orders].sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id);
        for (const o of sorted) {
            const key = o.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(o);
        }
        return Object.entries(map);
    }, [orders]);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Мои чеки
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            История продаж
                        </Typography>
                    </Box>
                </Box>

                {/* ── Быстрый выбор периода ────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', gap: 0.8, mb: 2 }}>
                        {periods.map(p => (
                            <Chip
                                key={p.label}
                                size="sm"
                                variant={activePeriod === p.label ? 'solid' : 'outlined'}
                                color={activePeriod === p.label ? 'primary' : 'neutral'}
                                onClick={() => selectPeriod(p)}
                                sx={{ px: 2, py: 0.5, ...inputSx }}
                            >
                                {p.label}
                            </Chip>
                        ))}
                    </Box>

                    {/* Ручной выбор дат */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                        <Input type="date" size="sm" value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                            sx={inputSx}
                        />
                        <Input type="date" size="sm" value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                            sx={inputSx}
                        />
                    </Box>
                </motion.div>

                {/* ── Итого-карточки ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1, mb: 2.5 }}>
                        <Box sx={{
                            gridColumn: '1 / -1',
                            px: 2, py: 1.8,
                            borderRadius: 'var(--joy-radius-lg)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'rgba(162,38,226,0.08)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <Box>
                                <Box sx={{ color: 'var(--joy-palette-primary-400)', mb: 0.5 }}>
                                    <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Typography sx={{ fontSize: 28, fontWeight: 800, color: 'var(--joy-palette-primary-300)', lineHeight: 1 }}>
                                    {fmtMoney(totals.total)} ₽
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                    Выручка · {totals.count} чек.
                                </Typography>
                            </Box>
                        </Box>
                        {[
                            { label: 'Перевод',   value: totals.transfer, color: '#818cf8', bg: 'rgba(129,140,248,0.08)', icon: <CreditCardRoundedIcon sx={{ fontSize: 16 }} /> },
                            { label: 'Наличные',  value: totals.cash,     color: '#22c55e', bg: 'rgba(34,197,94,0.08)', icon: <PaymentsRoundedIcon sx={{ fontSize: 16 }} /> },
                        ].map(s => (
                            <Box key={s.label} sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: s.bg }}>
                                <Box sx={{ color: s.color, mb: 0.3 }}>{s.icon}</Box>
                                <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{fmtMoney(s.value)}</Typography>
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>{s.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </motion.div>

                {/* ── Список чеков ──────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : orders.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет чеков за выбранный период
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1.5}>
                            {grouped.map(([date, items]) => {
                                const dayTotal = items.reduce((s, o) => s + parseFloat(o.value || 0), 0);
                                return (
                                    <motion.div key={date} variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            overflow: 'hidden',
                                        }}>
                                            {/* Заголовок дня */}
                                            <Box sx={{
                                                px: 2, py: 1,
                                                borderBottom: '1px solid var(--joy-palette-divider)',
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {fmtDate(date)}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-primary-300)', fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtMoney(dayTotal)} ₽
                                                </Typography>
                                            </Box>

                                            {/* Чеки */}
                                            {items.map((order, idx) => {
                                                const mc = MONEY_COLORS[order.money_type] || MONEY_COLORS.transfer;
                                                return (
                                                    <Box key={order.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        px: 2, py: 1,
                                                        borderBottom: idx < items.length - 1
                                                            ? '1px solid var(--joy-palette-divider)' : 'none',
                                                    }}>
                                                        {/* Инфо */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                                {order.location_name}
                                                            </Typography>
                                                            {order.created_by_name && (
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                                    Внёс: {order.created_by_name}
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        {/* Тип */}
                                                        <Chip size="sm" variant="soft" sx={{
                                                            fontSize: 10, fontWeight: 700,
                                                            bgcolor: mc.bg, color: mc.color,
                                                        }}
                                                            startDecorator={MONEY_ICONS[order.money_type]}
                                                        >
                                                            {MONEY_LABELS[order.money_type]}
                                                        </Chip>

                                                        {/* Сумма */}
                                                        <Typography sx={{
                                                            fontSize: 15, fontWeight: 800,
                                                            color: 'var(--joy-palette-text-primary)',
                                                            minWidth: 55, textAlign: 'right',
                                                            fontVariantNumeric: 'tabular-nums',
                                                        }}>
                                                            {fmtMoney(order.value)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </motion.div>
                                );
                            })}
                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}